import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 50;

const SkeletonRow = () => (
  <tr>
    {[...Array(9)]?.map((_, i) => (
      <td key={i} className="px-3 py-3">
        <div className="h-3 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: i === 0 ? '80px' : '60px' }} />
      </td>
    ))}
  </tr>
);

const formatNum = (n) => {
  if (!n && n !== 0) return '0';
  return Math.round(n)?.toLocaleString('fr-FR')?.replace(/\u202f/g, '\u00a0');
};

const fmtUSD = (n) => {
  if (!n || isNaN(n)) return '—';
  return '$' + Math.round(n)?.toLocaleString('fr-FR');
};

const CreatorCurrentTable = ({ data, loading, serverColor, getServerColor, basePath = '/admin/creators' }) => {
  const { t } = useTranslation();

  const COLUMNS = [
    { key: 'username', label: 'Username' },
    { key: 'display_name', label: 'Display Name' },
    { key: 'server', label: t('admin.performanceActuelle.colServer', 'Server') },
    { key: 'manager_name', label: 'Manager' },
    { key: 'diamonds', label: 'Diamonds' },
    { key: 'tier', label: 'Tier' },
    { key: 'activeness_level', label: 'Activeness' },
    { key: 'bonus_rankup', label: 'Bonus Rankup' },
    { key: 'bonus_activeness', label: 'Bonus Activeness' },
    { key: 'tier_status', label: t('admin.performanceActuelle.colStatus', 'Status') },
  ];
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState('diamonds');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    if (!searchQuery) return data || [];
    const q = searchQuery?.toLowerCase();
    return (data || [])?.filter(
      (r) =>
        (r?.username || '')?.toLowerCase()?.includes(q) ||
        (r?.display_name || '')?.toLowerCase()?.includes(q) ||
        (r?.tiktok_id || '')?.toLowerCase()?.includes(q)
    );
  }, [data, searchQuery]);

  const sorted = useMemo(() => {
    return [...filtered]?.sort((a, b) => {
      const aVal = a?.[sortKey];
      const bVal = b?.[sortKey];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted?.length / PAGE_SIZE));
  const paginated = sorted?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span style={{ color: 'rgba(255,255,255,0.15)' }}>↕</span>;
    return <span style={{ color: serverColor }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const getTierStatusStyle = (status) => {
    if (status === "Montée d'échelon") return { color: '#22c55e', bg: 'rgba(34,197,94,0.1)' };
    if (status === "Maintien d'échelon") return { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' };
    if (status === "Échelon non maintenu") return { color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
    return { color: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.06)' };
  };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      {/* Search */}
      <div className="px-4 pt-3" style={{ backgroundColor: '#0d0d0d' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e?.target?.value); setPage(1); }}
          placeholder={t('admin.performanceActuelle.searchCreator')}
          className="w-full rounded-lg px-3 py-2 text-base outline-none mb-3"
          style={{
            backgroundColor: '#1a1a1a',
            border: `1px solid ${searchQuery ? '#E5202A50' : '#2a2a2a'}`,
            color: 'rgba(255,255,255,0.8)',
            fontFamily: 'Inter, sans-serif',
            maxWidth: '320px',
            display: 'block',
          }}
        />
      </div>
      <div className="px-4 py-2 border-b flex items-center" style={{ borderColor: '#1f1f1f', backgroundColor: '#0d0d0d' }}>
        <span className="ml-auto text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.creatorsCount', { count: filtered?.length })}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ minWidth: '900px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
              {COLUMNS?.map((col) => (
                <th
                  key={col?.key}
                  className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                  style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}
                  onClick={() => handleSort(col?.key)}
                >
                  {col?.label} <SortIcon col={col?.key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? [...Array(10)]?.map((_, i) => <SkeletonRow key={i} />)
              : paginated?.map((row, idx) => {
                  const srvColor = getServerColor ? getServerColor(row?.server) : serverColor;
                  const tierStatusStyle = getTierStatusStyle(row?.tier_status);
                  return (
                    <tr
                      key={row?.tiktok_id || idx}
                      className="transition-colors cursor-pointer"
                      style={{ borderBottom: '1px solid #1a1a1a' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      onClick={() => navigate(`${basePath}/${row?.tiktok_id}`)}

                    >
                      {/* Username */}
                      <td className="px-3 py-3" style={{ maxWidth: '150px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span
                            className="text-sm font-medium text-white"
                            style={{ fontFamily: 'Inter, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}
                          >
                            {row?.username || '—'}
                          </span>
                          {row?.is_new_recruit === true && (
                            <span style={{
                              fontSize: '9px', fontWeight: '700',
                              padding: '1px 5px', borderRadius: '3px',
                              backgroundColor: 'rgba(34,197,94,0.15)',
                              color: '#22c55e', flexShrink: 0,
                            }}>NEW</span>
                          )}
                        </div>
                      </td>
                      {/* Display Name */}
                      <td className="px-3 py-3" style={{ maxWidth: '150px' }}>
                        <span
                          className="text-xs"
                          style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter, sans-serif', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}
                        >
                          {row?.display_name || '—'}
                        </span>
                      </td>
                      {/* Serveur */}
                      <td className="px-3 py-3">
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-md"
                          style={{ backgroundColor: `${srvColor}22`, color: srvColor, fontFamily: 'Sora, sans-serif' }}
                        >
                          {row?.server || '—'}
                        </span>
                      </td>
                      {/* Manager */}
                      <td className="px-3 py-3">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
                          {row?.manager_name || '—'}
                        </span>
                      </td>
                      {/* Diamonds */}
                      <td className="px-3 py-3">
                        <span className="text-sm font-semibold" style={{ color: srvColor, fontFamily: 'Inter, sans-serif' }}>
                          💎 {formatNum(row?.diamonds)}
                        </span>
                      </td>
                      {/* Tier */}
                      <td className="px-3 py-3">
                        {row?.tier_current != null ? (
                          <span
                            className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{
                              backgroundColor: 'rgba(107,114,128,0.15)',
                              color: '#9ca3af',
                              border: '1px solid rgba(107,114,128,0.3)',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            T{row?.tier_current}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                        )}
                      </td>
                      {/* Activeness */}
                      <td className="px-3 py-3">
                        {row?.activeness_level != null ? (
                          <span
                            className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{
                              backgroundColor: row?.activeness_level > 0 ? 'rgba(34,197,94,0.12)' : 'rgba(107,114,128,0.12)',
                              color: row?.activeness_level > 0 ? '#22c55e' : '#6b7280',
                              border: `1px solid ${row?.activeness_level > 0 ? 'rgba(34,197,94,0.3)' : 'rgba(107,114,128,0.3)'}`,
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            L{row?.activeness_level}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                        )}
                      </td>
                      {/* Bonus Rankup */}
                      <td className="px-3 py-3">
                        <span
                          className="text-xs font-semibold"
                          style={{ color: row?.bonus_rankup > 0 ? '#22c55e' : 'rgba(255,255,255,0.2)', fontFamily: 'Inter, sans-serif' }}
                        >
                          {row?.bonus_rankup > 0 ? fmtUSD(row?.bonus_rankup) : '—'}
                        </span>
                      </td>
                      {/* Bonus Activeness */}
                      <td className="px-3 py-3">
                        <span
                          className="text-xs font-semibold"
                          style={{ color: row?.bonus_activeness > 0 ? '#22c55e' : 'rgba(255,255,255,0.2)', fontFamily: 'Inter, sans-serif' }}
                        >
                          {row?.bonus_activeness > 0 ? fmtUSD(row?.bonus_activeness) : '—'}
                        </span>
                      </td>
                      {/* Statut */}
                      <td className="px-3 py-3">
                        {row?.tier_status ? (
                          <span
                            className="px-2 py-0.5 rounded text-xs font-medium"
                            style={{
                              backgroundColor: tierStatusStyle?.bg,
                              color: tierStatusStyle?.color,
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {row?.tier_status}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {!loading && (
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#1f1f1f' }}>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
            {t('admin.performanceActuelle.creatorsPage', { count: sorted?.length, page, total: totalPages })}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded text-xs font-medium transition-all disabled:opacity-30"
              style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif', border: '1px solid #2a2a2a' }}
            >
              {t('admin.performanceActuelle.prev')}
            </button>
            {[...Array(Math.min(5, totalPages))]?.map((_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = page - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className="px-3 py-1.5 rounded text-xs font-medium transition-all"
                  style={{
                    backgroundColor: page === pageNum ? 'rgba(229,32,42,0.15)' : '#1f1f1f',
                    color: page === pageNum ? '#E5202A' : 'rgba(255,255,255,0.5)',
                    border: `1px solid ${page === pageNum ? 'rgba(229,32,42,0.3)' : '#2a2a2a'}`,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded text-xs font-medium transition-all disabled:opacity-30"
              style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif', border: '1px solid #2a2a2a' }}
            >
              {t('admin.performanceActuelle.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorCurrentTable;
